import React, { useState } from 'react';
import moment from 'moment';

import Button from 'components/form/Button.component';
import S from 'constants/string.const';
import { fromEventToValue } from 'utils/converters/event.util';
import { fromAnyDateToZulu } from 'utils/converters/time/from.util';
import {
  formatDayOfWeek,
  formatDayToHour,
  formatDayToMonth,
  formatDayToMonthLongLocale,
  formatDayToNameToMonth,
  formatDayToShortYearOrdinal,
  formatDayToYeaCompact,
  formatDayToYear,
  formatDayToYearAveragedWeekday,
  formatDayToYearDetailed,
  formatDayToYearFullWeekday,
  formatDayToYearFullWeekdaySuffix,
  formatDayToYearLong,
  formatDayToYearMidWeekday,
  formatDayToYearOrdinal,
  formatDayToYearShorter,
  formatDayToYearShortWeekday,
  formatDayToYearStandard,
  formatDayToYearWeekday,
  formatDayToYearWeekdayInt,
  formatDayToYearWithTime,
  formatHourAndMinute,
  formatHourAndMinuteFull,
  formatHourAndMinuteHalf,
  formatHourAndMinuteShort,
  formatHourAndMinuteShortMeridiem,
  formatHourToYear,
  formatMonthLong,
  formatMonthToYearLong,
  formatPartAmPm,
  formatPartDate,
  formatPartTime,
  formatSegmentDay,
  formatSegmentWeek,
  formatUnixMillis,
  formatYearToDayCompact,
  formatYearToDayLocalized,
  formatYearToDayStandard,
  formatYearToSecondSn,
} from 'utils/date/format.util';
import { nowAsZulu } from 'utils/date/now.util';
import asInput from 'utils/strings/asInput.util';

import CN from './UiPage.module.scss'; // Since this is experiment, we can share the same .module.scss file

const DEFAULT_DATE = nowAsZulu();

const comboFormatter = (date) => moment(date).format(comboFormatter.FORMAT);
comboFormatter.FORMAT = 'ddd, D MMM Y [at] h:mma';

const lowerCaseFormatter = (d) => formatDayToYear(d).toLowerCase();
lowerCaseFormatter.FORMAT = `"${formatDayToYear.FORMAT}".toLowerCase()`;

const FORMATTERS = Object.freeze([
  { func: comboFormatter, name: 'comboFormatter' },
  { func: formatDayOfWeek, name: 'formatDayOfWeek' },
  { func: formatDayToYeaCompact, name: 'formatDayToYeaCompact' },
  { func: formatDayToYear, name: 'formatDayToYear' },
  { func: lowerCaseFormatter, name: 'lowerCaseFormatter' },
  { func: formatDayToYearShorter, name: 'formatDayToYearShorter' },
  { func: formatDayToYearDetailed, name: 'formatDayToYearDetailed' },
  { func: formatDayToYearWithTime, name: 'formatDayToYearWithTime' },
  { func: formatDayToMonth, name: 'formatDayToMonth' },
  { func: formatDayToMonthLongLocale, name: 'formatDayToMonthLongLocale' },
  { func: formatDayToNameToMonth, name: 'formatDayToNameToMonth' },
  { func: formatDayToYearLong, name: 'formatDayToYearLong' },
  { func: formatDayToYearStandard, name: 'formatDayToYearStandard' },
  { func: formatDayToYearOrdinal, name: 'formatDayToYearOrdinal' },
  { func: formatDayToShortYearOrdinal, name: 'formatDayToShortYearOrdinal' },
  { func: formatDayToYearWeekday, name: 'formatDayToYearWeekday' },
  { func: formatDayToYearFullWeekdaySuffix, name: 'formatDayToYearFullWeekdaySuffix' },
  { func: formatDayToYearWeekdayInt, name: 'formatDayToYearWeekdayInt' },
  { func: formatDayToYearShortWeekday, name: 'formatDayToYearShortWeekday' },
  { func: formatDayToYearMidWeekday, name: 'formatDayToYearMidWeekday' },
  { func: formatDayToYearFullWeekday, name: 'formatDayToYearFullWeekday' },
  { func: formatDayToYearAveragedWeekday, name: 'formatDayToYearAveragedWeekday' },
  { func: formatDayToHour, name: 'formatDayToHour' },
  { func: formatMonthLong, name: 'formatMonthLong' },
  { func: formatMonthToYearLong, name: 'formatMonthToYearLong' },
  { func: formatHourAndMinute, name: 'formatHourAndMinute' },
  { func: formatHourAndMinuteShortMeridiem, name: 'formatHourAndMinuteShortMeridiem' },
  { func: formatHourAndMinuteShort, name: 'formatHourAndMinuteShort' },
  { func: formatHourAndMinuteHalf, name: 'formatHourAndMinuteHalf' },
  { func: formatHourAndMinuteFull, name: 'formatHourAndMinuteFull' },
  { func: formatHourToYear, name: 'formatHourToYear' },
  { func: formatYearToDayLocalized, name: 'formatYearToDayLocalized' },
  { func: formatYearToDayStandard, name: 'formatYearToDayStandard' },
  { func: formatYearToDayCompact, name: 'formatYearToDayCompact' },
  { func: formatYearToSecondSn, name: 'formatYearToSecondSn' },
  { func: formatPartDate, name: 'formatPartDate' },
  { func: formatPartTime, name: 'formatPartTime' },
  { func: formatPartAmPm, name: 'formatPartAmPm' },
  { func: formatSegmentWeek, name: 'formatSegmentWeek' },
  { func: formatSegmentDay, name: 'formatSegmentDay' },
  { func: formatUnixMillis, name: 'formatUnixMillis' },
]);

const FormatRow = ({ date, order, formatter }) => (
  <div className={CN.horizontalFlex}>
    <div className={CN.example}>{order}</div>

    <div className={CN.example}>{formatter.func(date)}</div>

    <div className={CN.example}>
      <code>{formatter.func?.FORMAT}</code>
    </div>

    <div className={CN.example}>
      <strong>{formatter.name}</strong>
    </div>
  </div>
);

const DateTimePanelUi = () => {
  const [value, setValue] = useState(DEFAULT_DATE);
  const date = fromAnyDateToZulu(value);

  return (
    <div data-bem="DateTimePanelUi" className={CN.container}>
      <div className={CN.component}>
        For the examples, the BE response is: {date}.
        <br /> <br />
        You can change it manually:
        <input type="text" value={asInput(value)} style={{ width: '40ch' }} onChange={(ev) => setValue(fromEventToValue(ev))} />
        <br />
        or
        <Button onClick={() => setValue(DEFAULT_DATE)}>reset</Button> it.
        <br /> <br />
        The meaning of the formatting letters can be found
        {S.space}
        <a href="https://momentjs.com/docs/#/displaying/format/" target="_blank" rel="noreferrer">
          here
        </a>
      </div>

      <hr />

      <div className={CN.verticalFlex}>
        {FORMATTERS.map((formatter, index) => (
          <FormatRow date={date} key={index} order={index} formatter={formatter} />
        ))}
      </div>
    </div>
  );
};

export default DateTimePanelUi;
